import React, { useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { menusFrom, NOTICE_KEY } from "./menus";
import { MENU_ICONS } from "./icons";
import { useChatText } from "./text";
import { useLanguage } from "../../i18n/LanguageContext";
import { StoreCard, PeriodCard, InquiryCard, GoInqButton, PickList, HomeButton } from "./cards";
import "./chat.css";

const PHOTO_MENUS = ["menu", "printer", "pay", "other"];
// 말풍선 안에 함께 그리는 카드(시안: 답 글 아래 sq·내 문의 현황 열기). 나머지는 말풍선 밑에 따로.
const INLINE = ["inquiry", "inquiries", "goInq"];
// 시안 bdate — 「10. 14. 10:17」
const pad = (n) => String(n).padStart(2, "0");
const bdate = (d) => `${pad(d.getMonth() + 1)}. ${pad(d.getDate())}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;

// 가맹점주 챗봇 본문(시안 renderHome) — 환영 문구·메뉴 그리드 아래로 대화가 쌓인다
export default function ChatView({ chat, welcomeTitle, welcomeMessage, go, busyLabel, endRef, shown = true }) {
    const t = useChatText();
    const { lang } = useLanguage();
    const zh = lang === "zh";
    const active = (card) => !!chat.flow && chat.flow.id === card.flowId;
    const menus = menusFrom(chat.categories, lang);
    const photoStep = !!chat.flow && chat.flow.step === "text" && PHOTO_MENUS.includes(chat.flow.menu);

    const renderCard = (card) => {
        switch (card.type) {
            case "store": return <StoreCard card={card} active={active(card) && chat.flow.step === "confirm"} onAnswer={(yes) => chat.confirmStore(card.flowId, yes)} onHome={chat.cancel} />;
            case "periods": return <PeriodCard active={active(card) && chat.flow.step === "period"} onPick={(label) => chat.pickPeriod(card.flowId, label)} />;
            case "inquiry": return <><InquiryCard q={card.inquiry} go={go} /><div className="cf-mini">{t("cf.done.hint")}</div></>;
            case "inquiries": return (
                <div className="cf-col">
                    {card.items.map((q) => <InquiryCard key={q.id} q={q} go={go} withNote />)}
                    {card.goInq && <GoInqButton go={go} />}
                </div>
            );
            case "goInq": return <GoInqButton go={go} />;
            case "notices": return <PickList items={card.items} label={(n) => (zh && n.title_zh) || n.title} onPick={chat.openNotice} />;
            default: return null;
        }
    };

    const renderMsg = (m) => {
        const user = m.role === "user";
        const inline = m.card && INLINE.includes(m.card.type);
        const body = m.md ? (
            <div className={m.html ? "notice-md" : undefined}>
                {/* 관리자가 넣은 평문(공지)은 줄바꿈을 마크다운 줄바꿈으로 */}
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={m.html ? [rehypeRaw] : []}>
                    {m.breaks ? m.md.replace(/\n/g, "  \n") : m.md}
                </ReactMarkdown>
            </div>
        ) : m.text ? <span className="cf-text">{m.text}</span> : null;
        return (
            <div key={m.id} className={`chatbot-bubble ${user ? "user" : "assistant"}`}>
                {m.at && <div className={`bubble-date ${user ? "user" : ""}`}>{bdate(m.at)}</div>}
                {(body || inline) && (
                    <div className={`bubble-message ${user ? "user" : "assistant"}`}>
                        {body}
                        {inline && <div className="cf-inline">{renderCard(m.card)}</div>}
                    </div>
                )}
                {m.card && !inline && (m.card.type === "store" ? renderCard(m.card) : <div className="cf-card-wrap">{renderCard(m.card)}</div>)}
            </div>
        );
    };

    // 「처음으로」: 그 앞 말은 이전 내역(위로 스크롤하면 보임), 첫 화면은 아래에 새로 — 새로고침한 듯 첫 화면이 맨 위에 오게
    // 새 첫 화면 칸을 대화 창 높이만큼 늘려 맨 위로 올릴 자리를 만든다
    const freshRef = useRef(null);
    const past = chat.messages.filter((m) => m.id <= (chat.homeMark || 0));
    const current = chat.messages.filter((m) => m.id > (chat.homeMark || 0));
    const freshEmpty = useRef(true);
    freshEmpty.current = current.length === 0;
    useLayoutEffect(() => {
        const el = freshRef.current;
        const area = el?.closest(".chatbot-chat-area");
        if (!el) return undefined;
        if (!chat.homeMark || !past.length || !area) { el.style.minHeight = ""; return undefined; }
        const pad = () => { const cs = getComputedStyle(area); return [parseFloat(cs.paddingTop) || 0, parseFloat(cs.paddingBottom) || 0]; };
        const fit = () => { const [top, bottom] = pad(); el.style.minHeight = `${area.clientHeight - top - bottom}px`; };
        const toTop = (smooth) => {
            const top = area.scrollTop + el.getBoundingClientRect().top - area.getBoundingClientRect().top - pad()[0];
            if (smooth && area.scrollTo) area.scrollTo({ top, behavior: "smooth" }); else area.scrollTop = top;
        };
        fit();
        // 누른 때는 부드럽게 올라가고(움직임 줄이기 설정이면 바로), 첫 화면은 CSS로 떠오르듯 나타난다
        toTop(!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
        // 창 크기가 바뀌면(휴대폰 키보드 등) 높이를 다시 맞추고, 아직 새 말이 없으면 첫 화면을 다시 맨 위로
        if (typeof ResizeObserver === "undefined") return undefined;
        let h = area.clientHeight; // 감시를 시작하면 바로 한 번 불린다 — 높이가 그대로면 넘어가야 부드러운 스크롤이 끊기지 않는다
        const ro = new ResizeObserver(() => {
            if (area.clientHeight === h) return;
            h = area.clientHeight;
            fit();
            if (freshEmpty.current) toTop(false);
        });
        ro.observe(area);
        return () => ro.disconnect();
    }, [chat.homeMark]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className={`chatbot-chat-section ${shown ? "fade-in" : "fade-out"}`}>
            {past.length > 0 && <div className="cf-chat cf-past">{past.map(renderMsg)}</div>}
            <div ref={freshRef} key={chat.homeMark || 0} className={`cf-fresh ${chat.homeMark ? "cf-fresh-in" : ""}`}>
                <div className="after-loading">
                    <div className="chatbot-welecome-section">
                        <h1 className="chatbot-intro-title">{welcomeTitle}</h1>
                        <p className="chatbot-intro-text">{welcomeMessage}</p>
                    </div>
                    <div className="chatbot-button-grid">
                        {menus.map((m) => {
                            const notice = m.key === NOTICE_KEY;
                            const icon = notice ? MENU_ICONS.notice : MENU_ICONS[m.flow] || <span className="cf-emoji" aria-hidden="true">{m.cat.icon_emoji}</span>;
                            return (
                                <button key={m.key} type="button" className="chatbot-button cf-menu" onClick={() => chat.startMenu(m.key)}>
                                    <div className="chatbot-button-icon">{icon}</div>
                                    <div>
                                        <div className="chatbot-button-title">{notice ? t("notice.title") : m.title}</div>
                                        {(notice || m.desc) && <div className="chatbot-button-desc">{notice ? t("notice.desc") : m.desc}</div>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="cf-chat">
                    {current.map(renderMsg)}

                    {photoStep && (
                        <div className="cf-attach">
                            <label className="cf-btn">
                                {t("cf.attach", { n: chat.files.length })}
                                <input type="file" accept="image/*" multiple hidden onChange={(e) => { chat.addFiles(e.target.files); e.target.value = ""; }} />
                            </label>
                            {chat.files.length > 0 && (
                                <div className="file-preview-container">
                                    {chat.files.map((f, i) => (
                                        <div key={i} className="file-preview-item">
                                            <div className="file-preview-image-wrapper">
                                                {f.url && <img src={f.url} alt={f.file.name} className="file-preview-image" />}
                                                <button type="button" className="file-preview-remove" aria-label={t("common.deleteFile")} onClick={() => chat.removeFile(i)}>×</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 가맹점 확인 중에는 카드 안의 「처음으로」를 쓴다. 접수 중에도 자리는 지킨다(숨기면 높이가 바뀌어 화면이 움찔) */}
                    {chat.flow && chat.flow.step !== "confirm" && (
                        <div className="cf-home">
                            <HomeButton disabled={chat.flow.step === "submitting"} onClick={chat.cancel} />
                        </div>
                    )}

                    {(chat.busy || busyLabel) && (
                        <div className="chatbot-bubble assistant" role="status" aria-busy="true">
                            <div className="bubble-message assistant">{busyLabel || t("common.generating")}</div>
                        </div>
                    )}
                </div>
            </div>
            <div ref={endRef} />
        </section>
    );
}
